import axios from 'axios'
import isNil from 'lodash/isNil'
import isObject from 'lodash/isObject'
import trim from 'lodash/trim'
import { toast } from 'react-toastify'
import { getToken, HEADER_PREFIX } from 'utils/auth'
import { DELIVERY_METHODS, PAGINATION_SIZE } from 'utils/constants'
import {
  downloadFetchResponse,
  downloadFetchResponseZip,
} from 'utils/downloadManager'
import { hasText, isFilledArray } from 'utils/helpers'
import { getSentryHeaders } from 'utils/sentry'
import { uploadInChunks } from 'utils/uploadFileInChunks'

import { API, ApiBase } from './api'
import { handleResponseError } from './apiHelpers'
import {
  makeDeleteRequest,
  makeGetRequest,
  makeOptionsRequest,
  makePatchRequest,
  makePostRequest,
  makePutRequest,
} from './fetchCalls'
import { hasFetchSucceeded } from './fetchHandlers'

async function token() {
  const userToken = await getToken()
  return userToken ? `${HEADER_PREFIX} ${userToken}` : 'None'
}

async function getIpAddress() {
  try {
    const ipFetchResponse = await fetch('https://icanhazip.com/')
    return await ipFetchResponse.text()
  } catch (error) {
    return null
  }
}

export async function getTimeAndIP() {
  return await makeGetRequest(API.FORM.GET_TIME_AND_IP)
}

async function getPackets() {
  return await makeGetRequest(`${ApiBase}/packets`)
}

async function createPacket(packet) {
  return await makePostRequest(`${ApiBase}/packets`, {
    body: JSON.stringify(packet),
  })
}

async function getPacket(id) {
  return await makeGetRequest(`${ApiBase}/packets/${id}`)
}

async function updatePacket(id, body) {
  return await makePatchRequest(`${ApiBase}/packets/${id}`, {
    body: JSON.stringify(body),
  })
}

async function updateForm(id, body) {
  return await makePatchRequest(`${ApiBase}/forms/${id}`, {
    body: JSON.stringify(body),
  })
}

async function deletePacket(id) {
  return await makeDeleteRequest(`${ApiBase}/packets/${id}`)
}

async function enableDisableForm(uid, enabled) {
  return await makePostRequest(`${ApiBase}/forms/enable_disable`, {
    body: JSON.stringify({ uid, enabled }),
  })
}

async function enableDisablePacket(uid, enabled) {
  return await makePostRequest(`${ApiBase}/packets/enable_disable`, {
    body: JSON.stringify({ uid, enabled }),
  })
}

async function getResponses() {
  return await makeGetRequest(
    `${ApiBase}/responses?exclude_packet=true&is_submitted=true&is_archived=false&ordering=-submitted_at`
  )
}

async function getMessages(limit) {
  try {
    const queryLimit =
      isNil(limit) || isNaN(limit) ? PAGINATION_SIZE.MESSAGE : limit
    return await makeGetRequest(
      `${ApiBase}/messages?` +
        'is_submitted=false&' +
        'is_archived=false&' +
        'ordering=-submitted_at&' +
        `limit=${queryLimit}`
    )
  } catch (error) {}
}

async function getMoreMessages(startAt) {
  try {
    const sort = '-submitted_at'
    const resultCount = PAGINATION_SIZE.MESSAGE
    return await makeGetRequest(
      `${ApiBase}/messages?` +
        'is_submitted=false&' +
        'is_archived=false&' +
        `ordering=${sort}&` +
        `offset=${startAt}&` +
        `limit=${resultCount}`
    )
  } catch (error) {}
}

async function getMessagesSubmitted() {
  try {
    return await makeGetRequest(
      `${ApiBase}/messages?is_submitted=true&is_archived=false&ordering=-submitted_at&limit=${PAGINATION_SIZE.RESPONSE}`
    )
  } catch (error) {}
}

async function getMoreMessagesSubmitted(startAt) {
  try {
    return await makeGetRequest(
      `${ApiBase}/messages?is_submitted=true&is_archived=false&ordering=-submitted_at&offset=${startAt}&limit=${PAGINATION_SIZE.RESPONSE}`
    )
  } catch (error) {}
}

let requestClientsCancel

function buildURLWithQuery({
  baseURL,
  limitQuery,
  orderQuery,
  searchQuery,
  firstNameQuery,
  lastNameQuery,
}) {
  if (searchQuery) {
    return baseURL + limitQuery + searchQuery
  }

  if (!firstNameQuery && !lastNameQuery) {
    return baseURL + limitQuery + orderQuery
  }

  return `${baseURL}?${firstNameQuery ? `${firstNameQuery}&` : ''}${
    lastNameQuery || ''
  }`
}

async function getClients({
  paginatedURL = null,
  search = null,
  limit = 100,
  order = null,
  firstName = null,
  lastName = null,
}) {
  /**
   * @param {string} paginatedURL - URL to fetch paginated data from
   * @param {string} search - search query string
   * @param {number} limit - number of results to return
   * @param {string} order - order results by asc 'first_name' or desc '-first_name',
   *                         multiple fields can be ordered by seperated by commas 'first_name,-last_name'
   */
  let cancelToken = {}

  if (search || firstName || lastName) {
    if (requestClientsCancel) {
      requestClientsCancel.cancel()
    }
    requestClientsCancel = axios.CancelToken.source()
    cancelToken = { cancelToken: requestClientsCancel.token }
  }
  const baseURL = ApiBase + (paginatedURL || '/customers')
  const limitQuery = paginatedURL ? '' : `?limit=${limit || 9999}`
  const searchQuery = search ? `&search=${search}` : ''
  const firstNameQuery = firstName ? `&first_name=${firstName}` : ''
  const lastNameQuery = lastName ? `&last_name=${lastName}` : ''
  const orderQuery = order ? `&ordering=${order}` : ''
  const url = buildURLWithQuery({
    baseURL,
    limitQuery,
    orderQuery,
    searchQuery,
    firstNameQuery,
    lastNameQuery,
  })

  const res = await axios.get(url, {
    headers: {
      'content-type': 'application/json',
      Authorization: await token(),
      ...getSentryHeaders(),
    },
    ...cancelToken,
  })
  return res.data
}

export async function getClient(id, queryParams = null) {
  return await makeGetRequest(
    `${ApiBase}/customers/${id}${queryParams ? `?${queryParams}` : ''}`
  )
}

async function deleteClient(id) {
  await makeDeleteRequest(`${ApiBase}/customers/${id}`)
  return true
}

async function updateClient(id, dataToUpdate) {
  if (!isObject(dataToUpdate)) return {}
  const body = JSON.stringify(dataToUpdate)
  return await makePatchRequest(`${ApiBase}/customers/${id}`, {
    body,
  })
}

async function mergeClients(masterClientId, clientsId) {
  if (isNil(masterClientId) || !isFilledArray(clientsId)) {
    return {}
  }
  const body = JSON.stringify({ customers: clientsId })
  await makePostRequest(API.CLIENTS.MERGE(masterClientId), {
    body,
  })
  return true
}

async function getClientResponses(id) {
  try {
    const json = await makeGetRequest(
      `${ApiBase}/responses?customer=${id}&exclude_packet=true&ordering=-submitted_at`
    )
    return json?.results || []
  } catch (error) {
    return []
  }
}

async function getClientPacketResponses(id) {
  try {
    const json = await makeGetRequest(
      `${ApiBase}/packet_responses?customer=${id}&ordering=-submitted_at`
    )
    return json?.results || []
  } catch (error) {
    return []
  }
}

async function createClient(
  firstName,
  lastName,
  email,
  dateOfBirth,
  phoneNumber
) {
  try {
    const first_name = trim(firstName)
    const last_name = trim(lastName)
    const body = JSON.stringify({
      first_name,
      last_name,
      ...(hasText(email) ? { email } : {}),
      ...(hasText(dateOfBirth) ? { date_birth: dateOfBirth } : {}),
      ...(hasText(phoneNumber) ? { phone_number: phoneNumber } : {}),
    })
    return await makePostRequest(`${ApiBase}/customers`, {
      body,
    })
  } catch (error) {}
}

async function getFormDetails(id) {
  return await makeGetRequest(`${ApiBase}/forms/${id}`)
}

async function getOrCreateCustomer({
  dateOfBirth,
  email,
  firstName,
  middleName,
  lastName,
  phone,
  resourceId,
  resourceType,
  messageType,
}) {
  const first_name = trim(firstName)
  const last_name = trim(lastName)
  const middle_name = trim(middleName)
  const phone_number = hasText(phone) ? phone.replace(/\D/g, '') : phone

  return await makePostRequest(`${ApiBase}/customers:get_or_create`, {
    body: JSON.stringify({
      first_name,
      middle_name,
      last_name,
      ...(hasText(email) ? { email } : {}),
      ...(hasText(dateOfBirth) ? { date_birth: dateOfBirth } : {}),
      ...(hasText(phone_number) ? { phone_number } : {}),
      [resourceType]: resourceId,
      message_type: messageType,
    }),
  })
}

async function startForm(formId, customerId) {
  return await makePostRequest(`${ApiBase}/response`, {
    body: JSON.stringify({
      form: formId,
      customer: customerId,
    }),
  })
}

async function submitResponse(id, mode, mannuallySubmitted = undefined) {
  const formData = new FormData()
  formData.append('is_submitted', true)

  if (mannuallySubmitted) {
    formData.append('mannually_submitted', true)
  }

  return await makePatchRequest(
    `${ApiBase}/response/${id}/submit?mode=${mode}`,
    {
      headers: {
        Authorization: await token(),
      },
      body: formData,
    }
  )
}

async function createResponse(customerId, formId, packetResponseId) {
  return await makePostRequest(`${ApiBase}/response`, {
    body: JSON.stringify({
      customer: customerId,
      form: formId,
      packet_response: packetResponseId,
    }),
  })
}

async function getResponseAnswers(responseId) {
  const json = await makeGetRequest(`${ApiBase}/response/${responseId}/answers`)
  return json?.answers ?? []
}

async function updateResponseVisited(
  responseId,
  { formIndex = undefined, hasVisited = undefined }
) {
  return await makePostRequest(`${ApiBase}/response/${responseId}/visited`, {
    body: JSON.stringify({
      form_index: formIndex,
      has_visited: hasVisited,
    }),
  })
}

async function updatePacketResponseVisited(packetResponseId, formIndex) {
  return await makePostRequest(
    `${ApiBase}/packet_responses/${packetResponseId}/visited`,
    {
      body: JSON.stringify({
        form_index: formIndex,
      }),
    }
  )
}

async function downloadEmptyForm(id, filename) {
  const res = await fetch(`${ApiBase}/forms/output/${id}.pdf`, {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      Authorization: await token(),
    },
  })
  await downloadFetchResponse(res, filename)
}

async function downloadResponse(id, filename) {
  const res = await fetch(`${ApiBase}/response/output/${id}`, {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      Authorization: await token(),
    },
  })
  await downloadFetchResponse(res, filename)
}

async function downloadPacketResponseZip(id, filename) {
  const res = await fetch(`${ApiBase}/packet_responses/output/${id}.zip`, {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      Authorization: await token(),
    },
  })
  if (!hasFetchSucceeded(res)) return false
  await downloadFetchResponseZip(res, filename)
  return true
}

async function downloadPacketResponsePdf(id, filename) {
  const res = await fetch(`${ApiBase}/packet_responses/output/${id}.pdf`, {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      Authorization: await token(),
    },
  })
  if (!hasFetchSucceeded(res)) return false
  await downloadFetchResponse(res, filename)
  return true
}

async function archiveResponse(id) {
  return await makePatchRequest(`${ApiBase}/response/${id}`, {
    body: JSON.stringify({
      is_archived: 'true',
    }),
  })
}

async function deleteResponse(id) {
  await makeDeleteRequest(`${ApiBase}/response/${id}`)
  return true
}

async function unarchiveResponse(id) {
  return await makePatchRequest(`${ApiBase}/response/${id}`, {
    body: JSON.stringify({
      is_archived: 'false',
    }),
  })
}

async function getResponsesPacket() {
  return await makeGetRequest(
    `${ApiBase}/packet_responses?is_submitted=true&is_archived=false&ordering=-submitted_at`
  )
}

export async function getResponsePacket(id) {
  return await makeGetRequest(`${ApiBase}/packet_responses/${id}`)
}

export async function getPublicResponsePacket(id, queryParams) {
  let url = `${ApiBase}/packet_responses/${id}/public`

  if (queryParams) {
    const params = new URLSearchParams(queryParams)
    url += `?${params}`
  }

  return await makeGetRequest(url)
}

async function submitResponsePacket(id, mode, mannuallySubmitted = undefined) {
  return await makePatchRequest(
    `${ApiBase}/packet_responses/${id}/submit?mode=${mode}`,
    {
      body: JSON.stringify({
        is_submitted: true,
        ...(mannuallySubmitted ? { mannually_submitted: true } : {}),
      }),
    }
  )
}

export async function resendMessageCodeLink(code) {
  await makePostRequest(`${ApiBase}/message/resend_code`, {
    body: JSON.stringify({
      code,
    }),
  })
  return true
}

async function archivePacketResponse(id) {
  return await makePatchRequest(`${ApiBase}/packet_responses/${id}`, {
    body: JSON.stringify({
      is_archived: 'true',
    }),
  })
}

async function deletePacketResponse(id) {
  await makeDeleteRequest(`${ApiBase}/packet_responses/${id}`)
  return true
}

async function unarchivePacketResponse(id) {
  return await makePatchRequest(`${ApiBase}/packet_responses/${id}`, {
    body: JSON.stringify({
      is_archived: 'false',
    }),
  })
}

export async function getSignupOptions() {
  return await makeOptionsRequest(API.AUTH.SIGNUP)
}

async function saveAnswer(
  responseId,
  questionId = '',
  answerRaw,
  choice_details
) {
  try {
    const res = await fetch(`${ApiBase}/answers`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: await token(),
      },
      body: JSON.stringify({
        answer_raw: answerRaw,
        question: questionId,
        response: responseId,
        ...(isNil(choice_details) ? {} : { choice_details }),
      }),
    })
    if (res.ok === false) {
      const json = await res?.json?.()
      return await handleResponseError?.(res, {
        answer_raw: answerRaw,
        question: questionId,
        response: responseId,
        ...(isNil(choice_details) ? {} : { choice_details }),
        json,
      })
    }
    return await res.json()
  } catch (error) {
    throw error
  }
}

async function deleteAnswer(questionId = '') {
  try {
    await makeDeleteRequest(`${ApiBase}/answers/${questionId}`)
    return true
  } catch (error) {
    throw error
  }
}

// Updated saveAnswerFiles function
async function saveAnswerFiles(responseId, questionId, files, mode) {
  try {
    // Delete all existing files for this question
    await deleteAnswerFile(responseId, questionId)

    const uploadPromises = files.map((file) =>
      uploadInChunks({
        file,
        responseId,
        questionId,
        mode,
        endpoint: 'answer_files',
      })
    )

    return await Promise.all(uploadPromises)
  } catch (error) {
    throw error
  }
}

async function saveAnswerFile(responseId, questionId, file, mode) {
  try {
    const data = new FormData()

    data.append('file', file)
    data.append('question', questionId)
    data.append('response', responseId)

    const options = {
      method: 'POST',
      headers: {
        Authorization: await token(),
      },
      body: data,
    }

    const res = await fetch(`${ApiBase}/answer_files?mode=${mode}`, options)
    if (res.ok === false) {
      const json = await res?.json?.()
      return await handleResponseError?.(res, { ...data, json })
    }
    return await res.json()
  } catch (error) {
    throw error
  }
}

async function deleteAnswerFile(responseId, questionId = '') {
  try {
    await makeDeleteRequest(`${ApiBase}/answer_files`, {
      body: JSON.stringify({
        response: responseId,
        question: questionId,
        file: null,
      }),
    })
    return true
  } catch (error) {
    throw error
  }
}

async function createMatrixRow(responseId, questionId = '') {
  const res = await fetch(`${ApiBase}/answer_matrix_rows`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: await token(),
    },
    body: JSON.stringify({
      response: responseId,
      question: questionId,
    }),
  })
  if (res.ok === false) {
    const json = await res?.json?.()
    return await handleResponseError?.(res, {
      response: responseId,
      question: questionId,
      json,
    })
  }
  return await res.json()
}

async function createPublicFormMessage({
  customerId,
  formId,
  teamId,
  isTablet,
  document,
  anonymous,
}) {
  try {
    return await makePostRequest(`${ApiBase}/message`, {
      body: JSON.stringify({
        target_email: null,
        customer: customerId,
        message_type: isTablet
          ? 'tablet_response'
          : anonymous
            ? 'anonymous_public_response'
            : 'public_response',
        form: formId,
        team: teamId,
        document,
      }),
    })
  } catch (error) {}
}

async function createPublicPacketMessage(
  customerId,
  packetId,
  teamId,
  isTablet
) {
  return await makePostRequest(`${ApiBase}/message`, {
    body: JSON.stringify({
      target_email: null,
      customer: customerId,
      message_type: isTablet ? 'tablet_response' : 'public_response',
      packet: packetId,
      team: teamId,
    }),
  })
}

async function createKioskFormMessage(customerId, formId, teamId) {
  try {
    return await makePostRequest(`${ApiBase}/message`, {
      body: JSON.stringify({
        target_email: null,
        customer: customerId,
        message_type: 'kiosk_response',
        form: formId,
        team: teamId,
      }),
    })
  } catch (error) {}
}

async function createKioskPacketMessage(customerId, packetId, teamId) {
  return await makePostRequest(`${ApiBase}/message`, {
    body: JSON.stringify({
      target_email: null,
      customer: customerId,
      message_type: 'kiosk_response',
      packet: packetId,
      team: teamId,
    }),
  })
}

async function sendMessageToCustomer({
  targetEmail,
  customer,
  preferredDeliveryMethod,
  deliveryMethodUsed,
  messageType,
  formId,
  packetId,
  is_waiting,
  team,
  phoneNumber,
  documentId = null,
  scheduledDate = null,
}) {
  return await makePostRequest(`${ApiBase}/message`, {
    body: JSON.stringify({
      target_email: targetEmail,
      target_phone: phoneNumber,
      customer,
      preferred_delivery_method: preferredDeliveryMethod,
      delivery_method_used: deliveryMethodUsed,
      message_type: messageType,
      form: formId,
      packet: packetId,
      document: documentId,
      is_waiting,
      team,
      scheduled_at: scheduledDate,
    }),
  })
}

async function sendMessageToCustomerEditLater({
  targetEmail,
  customer,
  preferredDeliveryMethod,
  messageType,
  formId,
  packetId,
  is_waiting,
  team,
  phoneNumber,
  packet_response,
  form_response,
}) {
  const body = {
    target_email: targetEmail,
    target_phone: phoneNumber,
    customer,
    preferred_delivery_method: preferredDeliveryMethod,
    message_type: messageType,
    ...(hasText(formId) ? { form: formId } : {}),
    packet: packetId,
    is_waiting,
    team,
  }
  if (packet_response) {
    body.packet_response = packet_response
  }
  if (form_response) {
    body.form_response = form_response
  }
  return await makePostRequest(`${ApiBase}/message/send`, {
    body: JSON.stringify(body),
  })
}

async function sendMessageReminder(messageId, newEmailOrPhone, deliveryType) {
  try {
    if (isNil(messageId)) {
      toast.error('Something went wrong, try refreshing the page')
      throw new Error('Something went wrong, try refreshing the page')
    }

    const body =
      deliveryType === DELIVERY_METHODS.SMS
        ? JSON.stringify({
            ...(hasText(newEmailOrPhone)
              ? { phone_number: newEmailOrPhone }
              : {}),
          })
        : JSON.stringify({
            ...(hasText(newEmailOrPhone) ? { email: newEmailOrPhone } : {}),
          })

    const response = await fetch(`${ApiBase}/remind/${messageId}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: await token(),
      },
      body,
    })

    if (response?.ok === true) {
      toast.info('Reminder Sent')
      return await response.json()
    }
    await response.json()
    toast.error(
      response?.statusText || 'Something went wrong, try refreshing the page'
    )
    throw new Error(response?.statusText)
  } catch (error) {}
}

async function updateMessage(messageId, updateData) {
  if (!isObject(updateData)) return {}
  return await makePatchRequest(`${ApiBase}/message/${messageId}`, {
    body: JSON.stringify(updateData),
  })
}

async function deleteMessage(messageId) {
  await makeDeleteRequest(`${ApiBase}/message/${messageId}`)
  return true
}

export async function getForm(id) {
  return await makeGetRequest(`${ApiBase}/forms/${id}`)
}

async function createExport(body) {
  return await makePostRequest(`${ApiBase}/exports`, {
    body: JSON.stringify(body),
  })
}

async function updateExport(id, body) {
  return await makePatchRequest(`${ApiBase}/exports/${id}`, {
    body: JSON.stringify(body),
  })
}

async function deleteExport(id) {
  await makeDeleteRequest(`${ApiBase}/exports/${id}`)
  return true
}

async function downloadedExport(id) {
  return await makePostRequest(`${ApiBase}/exports/${id}/downloaded`)
}

async function getTeam(id) {
  return await makeGetRequest(`${ApiBase}/teams/${id}`)
}

async function updateTeamDetails(id, dataToUpdate) {
  if (!isObject(dataToUpdate)) return {}
  const body = JSON.stringify(dataToUpdate)
  return await makePatchRequest(`${ApiBase}/teams/${id}`, {
    body,
  })
}

async function updateTeamImage(id, file) {
  const data = new FormData()

  data.append('image', file)

  const options = {
    headers: {
      Authorization: await token(),
    },
    body: data,
  }
  const json = await makePatchRequest(`${ApiBase}/teams/${id}`, options)
  return json?.image
}

async function getTeamConfig(id) {
  return await makeGetRequest(API.TEAM.TEAM_CONFIG_ID(id))
}

async function updateTeamConfig(id, dataToUpdate) {
  return await makePatchRequest(API.TEAM.TEAM_CONFIG_ID(id), {
    body: JSON.stringify(dataToUpdate),
  })
}

async function inviteUser(first_name, last_name, email, role) {
  if (
    !hasText(first_name) ||
    !hasText(last_name) ||
    !hasText(email) ||
    !hasText(role)
  ) {
    return null
  }
  return makePostRequest(API.TEAM.INVITE_USER, {
    body: JSON.stringify({
      first_name,
      last_name,
      email,
      role,
    }),
  })
}

async function getStripeConfig() {
  return await makeGetRequest(API.PAYMENT.CONFIG)
}

async function getPlans() {
  return await makeGetRequest(API.PAYMENT.PLANS)
}

async function getStripeSessionURL() {
  return makePostRequest(API.PAYMENT.PORTAL_SESSION)
}

async function createSetupIntent() {
  return makePostRequest(API.PAYMENT.SETUP_INTENT)
}

async function setDefaultPayment(setup_intent, client_secret) {
  return makePostRequest(API.PAYMENT.SET_DEFAULT_PAYMENT, {
    body: JSON.stringify({
      setup_intent,
      client_secret,
    }),
  })
}

async function connectStripe() {
  return makePostRequest(API.STRIPE.ACCOUNT)
}

async function disconnectStripe() {
  return makePostRequest(API.STRIPE.ACCOUNT, {
    body: JSON.stringify({
      disconnect: true,
    }),
  })
}

async function getGoogleIntegration() {
  return makeGetRequest(API.GOOGLE.INTEGRATION)
}

async function postGoogleIntegration(code) {
  return makePostRequest(API.GOOGLE.INTEGRATION, {
    body: JSON.stringify({
      code,
    }),
  })
}

async function deleteGoogleIntegration() {
  return makeDeleteRequest(API.GOOGLE.INTEGRATION)
}

async function getStripeAccountStatus() {
  return makeGetRequest(API.STRIPE.ACCOUNT)
}

async function cloneForm(formId) {
  return makePostRequest(API.FORM.CLONE(formId))
}

async function getElationCredentials() {
  return makeGetRequest(API.TEAM.ELATION_CREDENTIALS)
}

async function updateElationCredentials(data) {
  const payload = {}

  if (data.username) {
    payload.username = data.username
  }

  if (data.password) {
    payload.password = data.password
  }

  return makePutRequest(API.TEAM.ELATION_CREDENTIALS, {
    body: JSON.stringify(payload),
  })
}

async function getMyElationSyncs() {
  return makeGetRequest(API.ELATION.MY_SYNCS)
}

async function createElationSync() {
  return makePostRequest(API.ELATION.MY_SYNCS)
}

async function deleteForm(formId) {
  return makeDeleteRequest(API.FORM_BUILDER.FORM_ID(formId))
}
async function getTemplates() {
  return await makeGetRequest(API.TEMPLATES.LIST)
}

async function getTemplate(id) {
  return await makeGetRequest(API.TEMPLATES.DETAIL(id))
}

async function postTemplate(id) {
  await makePostRequest(`${ApiBase}/templates/${id}/forms`)
}

async function getSocialProviders() {
  return await makeGetRequest(API.AUTH.SOCIAL_PROVIDERS)
}

async function getSocialTeams() {
  return await makeGetRequest(API.TEAM.SOCIAL_TEAM)
}

async function createSocialTeam(
  provider,
  domain,
  enabled,
  allowSignup,
  allowCommonDomains
) {
  return await makePostRequest(API.TEAM.SOCIAL_TEAM, {
    body: JSON.stringify({
      provider,
      domain,
      enabled,
      allow_signup: allowSignup,
      allow_common_domains: allowCommonDomains,
    }),
  })
}

async function updateSocialTeam(
  id,
  provider,
  domain,
  enabled,
  allowSignup,
  allowCommonDomains
) {
  return await makePutRequest(`${API.TEAM.SOCIAL_TEAM}/${id}/`, {
    body: JSON.stringify({
      provider,
      domain,
      enabled,
      allow_signup: allowSignup,
      allow_common_domains: allowCommonDomains,
    }),
  })
}

async function deleteSocialTeam(id) {
  return await makeDeleteRequest(`${API.TEAM.SOCIAL_TEAM}/${id}/`)
}

async function calculateFormulaResult(questionId, answers, templateId = null) {
  let url = `${API.FORM.CALCULATION_RESULT(questionId)}`

  if (templateId) {
    url = `${API.FORM.CALCULATION_RESULT(questionId)}?templateId=${templateId}`
  }
  return await makePostRequest(url, {
    body: JSON.stringify(answers),
  })
}

async function getAppointments() {
  return await makeGetRequest(API.CALENDAR.APPOINTMENTS)
}

async function createAppointment(data) {
  return await makePostRequest(API.CALENDAR.APPOINTMENTS, {
    body: JSON.stringify(data),
  })
}

async function updateAppointment(id, data) {
  return await makePatchRequest(`${API.CALENDAR.APPOINTMENTS}/${id}`, {
    body: JSON.stringify(data),
  })
}

async function deleteAppointment(id) {
  return await makeDeleteRequest(`${API.CALENDAR.APPOINTMENTS}/${id}`)
}

async function getWorkflows() {
  return await makeGetRequest(API.CALENDAR.WORKFLOWS)
}

async function createWorkflow(data) {
  return await makePostRequest(API.CALENDAR.WORKFLOWS, {
    body: JSON.stringify(data),
  })
}

async function updateWorkflow(id, data) {
  return await makePutRequest(`${API.CALENDAR.WORKFLOWS}/${id}`, {
    body: JSON.stringify(data),
  })
}

async function deleteWorkflow(id) {
  return await makeDeleteRequest(`${API.CALENDAR.WORKFLOWS}/${id}`)
}

async function getAppointmentTypes() {
  return await makeGetRequest(API.CALENDAR.APPOINTMENT_TYPES)
}

async function createAppointmentType(data) {
  return await makePostRequest(API.CALENDAR.APPOINTMENT_TYPES, {
    body: JSON.stringify(data),
  })
}

async function updateAppointmentType(id, data) {
  return await makePatchRequest(`${API.CALENDAR.APPOINTMENT_TYPES}/${id}/`, {
    body: JSON.stringify(data),
  })
}

async function deleteAppointmentType(id) {
  return await makeDeleteRequest(`${API.CALENDAR.APPOINTMENT_TYPES}/${id}/`)
}

async function canSyncAgainProviderAvailability() {
  return await makeGetRequest(`${API.CALENDAR.PROVIDER_AVAILABILITY}/sync`)
}

async function syncProviderAvailability() {
  return await makePostRequest(`${API.CALENDAR.PROVIDER_AVAILABILITY}/sync`)
}

async function getElationProviders() {
  return await makeGetRequest(API.ELATION.PROVIDERS)
}

async function updateUser(id, data) {
  return await makePatchRequest(API.TEAM.TEAM_USER_ID(id), {
    body: JSON.stringify(data),
  })
}

export async function createUpdateResourceSettings(id, data) {
  return makePatchRequest(API.FORM_BUILDER.RESOURCE_SETTINGS_ID(id), {
    body: JSON.stringify(data),
  })
}

export async function getResourceSettings(id) {
  return makeGetRequest(API.FORM_BUILDER.RESOURCE_SETTINGS_ID(id))
}

export async function getPublicMessage(messageCode) {
  return makeGetRequest(API.MESSAGE.PUBLIC_MESSAGE(messageCode))
}

export async function invalidateMessageCode({
  messageCode,
  targetEmail = undefined,
}) {
  return makePostRequest(API.MESSAGE.INVALIDATE_MESSAGE, {
    body: JSON.stringify({
      message_code: messageCode,
      target_email: targetEmail,
    }),
  })
}

export async function listPublicInsuranceCompanies({
  messageCode,
  packetMessageCode,
}) {
  return makeGetRequest(
    API.FORM.PUBLIC_INSURANCE_COMPANIES({ messageCode, packetMessageCode })
  )
}

export async function listInsuranceCompanies() {
  return makeGetRequest(API.FORM.INSURANCE_COMPANIES)
}

export async function getQuestionValidationTypes() {
  return makeGetRequest(API.FORM_BUILDER.BUILDER_QUESTION_VALIDATION_TYPES)
}

export async function saveAllAnswers(data, mode) {
  return makePostRequest(`${ApiBase}/answers/save_all?mode=${mode}`, {
    body: JSON.stringify(data),
  })
}

const API_FUNCTIONS = {
  archivePacketResponse,
  archiveResponse,
  calculateFormulaResult,
  cloneForm,
  connectStripe,
  canSyncAgainProviderAvailability,
  createAppointment,
  createAppointmentType,
  createClient,
  createElationSync,
  createExport,
  createKioskFormMessage,
  createKioskPacketMessage,
  createMatrixRow,
  createPacket,
  createPublicFormMessage,
  createPublicPacketMessage,
  createResponse,
  createSetupIntent,
  createSocialTeam,
  createWorkflow,
  deleteAnswer,
  deleteAnswerFile,
  deleteAppointment,
  deleteAppointmentType,
  deleteClient,
  deleteExport,
  downloadedExport,
  deleteForm,
  deleteGoogleIntegration,
  deleteMessage,
  deletePacket,
  deletePacketResponse,
  deleteResponse,
  deleteSocialTeam,
  deleteWorkflow,
  disconnectStripe,
  downloadEmptyForm,
  downloadPacketResponsePdf,
  downloadPacketResponseZip,
  downloadResponse,
  enableDisableForm,
  enableDisablePacket,
  getAppointmentTypes,
  getAppointments,
  getClient,
  getClientPacketResponses,
  getClientResponses,
  getClients,
  getElationCredentials,
  getElationProviders,
  getForm,
  getPacket,
  getFormDetails,
  getGoogleIntegration,
  getIpAddress,
  getMessages,
  getMessagesSubmitted,
  getMoreMessages,
  getMoreMessagesSubmitted,
  getMyElationSyncs,
  getOrCreateCustomer,
  getPackets,
  getPlans,
  getQuestionValidationTypes,
  getResponseAnswers,
  getResponsePacket,
  getPublicResponsePacket,
  getResponses,
  getResponsesPacket,
  getSocialProviders,
  getSocialTeams,
  getStripeAccountStatus,
  getStripeConfig,
  getStripeSessionURL,
  getTeam,
  getTeamConfig,
  getTemplate,
  getTemplates,
  getTimeAndIP,
  getWorkflows,
  inviteUser,
  listPublicInsuranceCompanies,
  listInsuranceCompanies,
  mergeClients,
  postGoogleIntegration,
  postTemplate,
  resendMessageCodeLink,
  saveAnswer,
  saveAnswerFile,
  saveAnswerFiles,
  sendMessageReminder,
  sendMessageToCustomer,
  sendMessageToCustomerEditLater,
  setDefaultPayment,
  startForm,
  submitResponse,
  submitResponsePacket,
  syncProviderAvailability,
  unarchivePacketResponse,
  unarchiveResponse,
  updateAppointment,
  updateAppointmentType,
  updateClient,
  updateElationCredentials,
  updateExport,
  updateForm,
  updateMessage,
  updatePacket,
  updateSocialTeam,
  updateTeamConfig,
  updateTeamDetails,
  updateTeamImage,
  updateUser,
  updateWorkflow,
  updatePacketResponseVisited,
  updateResponseVisited,
  saveAllAnswers,
}

export default API_FUNCTIONS
